import Swiper from 'swiper';
import { Navigation } from 'swiper/modules';
import 'swiper/css';

/** @type {WeakMap<HTMLElement, () => void>} */
const resizeHandlers = new WeakMap();

/** @type {WeakMap<HTMLElement, number>} last equalized height per section */
const appliedHeights = new WeakMap();

/**
 * Set every card in the section to the height of the tallest card.
 * @param {HTMLElement} section
 */
export function equalizeClientTestimonialCardHeights(section) {
	const cards = [...section.querySelectorAll('.client-testimonials__card')];
	if (!cards.length) {
		return;
	}

	cards.forEach((card) => {
		card.style.minHeight = '';
	});

	const maxHeight = Math.max(
		...cards.map((card) => Math.ceil(card.getBoundingClientRect().height)),
		0,
	);

	if (maxHeight > 0) {
		const heightPx = `${maxHeight}px`;
		cards.forEach((card) => {
			card.style.minHeight = heightPx;
		});
	}

	// Poke Swiper only when the equalized height actually changed. This runs
	// on every window resize, and iOS fires a stream of (height-only) resize
	// events while the browser toolbar collapses/expands during normal page
	// scrolling; in cssMode swiper.update() writes wrapper.scrollLeft, so
	// calling it repeatedly mid-scroll fights the native scroll-snap and made
	// the carousel visibly jitter while scrolling the page vertically.
	const previous = appliedHeights.get(section);
	appliedHeights.set(section, maxHeight);
	if (previous === maxHeight) {
		return;
	}

	const swiperEl = section.querySelector('.js-client-testimonials-swiper');
	if (swiperEl?.swiper) {
		swiperEl.swiper.update();
	}
}

/**
 * @param {HTMLElement} section
 */
function bindEqualHeightResize(section) {
	const existing = resizeHandlers.get(section);
	if (existing) {
		window.removeEventListener('resize', existing);
	}

	let frame = 0;
	// Height-only resizes are the iOS toolbar showing/hiding during vertical
	// page scroll — card heights depend on the viewport WIDTH (via vw-based
	// card sizing), so only a width change warrants re-equalizing at all.
	let lastWidth = window.innerWidth;
	const onResize = () => {
		if (window.innerWidth === lastWidth) {
			return;
		}
		lastWidth = window.innerWidth;
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(() => equalizeClientTestimonialCardHeights(section));
	};

	resizeHandlers.set(section, onResize);
	window.addEventListener('resize', onResize, { passive: true });
}

/**
 * @param {HTMLElement} section
 */
function initSectionSwiper(section) {
	const el = section.querySelector('.js-client-testimonials-swiper');
	const prevBtn = section.querySelector('.js-client-testimonials-prev');
	const nextBtn = section.querySelector('.js-client-testimonials-next');
	const nav = section.querySelector('.client-testimonials__nav');
	const slides = el?.querySelectorAll('.swiper-slide') ?? [];

	if (!el) {
		return null;
	}

	if (slides.length < 2) {
		nav?.setAttribute('hidden', 'hidden');
	}

	// Hide the nav when all slides fit (locked); show it when scrolling is needed.
	const toggleNav = (sw) => nav?.toggleAttribute('hidden', sw.isLocked);

	bindEqualHeightResize(section);

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(() => {
			requestAnimationFrame(() => equalizeClientTestimonialCardHeights(section));
		});
		section.querySelectorAll('.client-testimonials__card-body').forEach((body) => observer.observe(body));
	}

	requestAnimationFrame(() => equalizeClientTestimonialCardHeights(section));

	if (slides.length < 2) {
		return null;
	}

	// iOS (WebKit — Safari AND Chrome there) kept failing on this carousel in
	// transform mode: first blank cards past a certain scroll depth, then a
	// frozen frame where tapping next visibly did nothing — all symptoms of
	// WebKit not recompositing the translate3d()'d .swiper-wrapper. Rather
	// than keep nudging the compositor (backdrop-filter removal, layer
	// promotion, clipping — none fully fixed it), switch touch devices to
	// Swiper's cssMode: native scroll + scroll-snap, no wrapper transform at
	// all, so that whole WebKit bug family no longer applies. Desktop keeps
	// transform mode (mouse-drag UX there is better without native scroll).
	const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;

	const swiper = new Swiper(el, {
		modules: [Navigation],
		slidesPerView: 'auto',
		spaceBetween: 20,
		watchOverflow: true,
		cssMode: isCoarsePointer,
		// This card bleeds toward the screen edge on mobile; without this, a
		// drag starting near the edge can lose the gesture to iOS Safari's
		// own swipe-back navigation mid-scroll instead of panning the
		// carousel (#128). (Inert in cssMode — native scroll handles touch.)
		edgeSwipeDetection: 'prevent',
		observer: true,
		observeParents: true,
		navigation: {
			prevEl: prevBtn,
			nextEl: nextBtn,
		},
		breakpoints: {
			640: {
				spaceBetween: 20,
			},
			1100: {
				spaceBetween: 20,
			},
		},
		on: {
			init(sw) {
				toggleNav(sw);
				requestAnimationFrame(() => equalizeClientTestimonialCardHeights(section));
			},
			lock: toggleNav,
			unlock: toggleNav,
		},
	});

	requestAnimationFrame(() => equalizeClientTestimonialCardHeights(section));

	return swiper;
}
export function initClientTestimonialsSwiper() {
	document.querySelectorAll('.js-client-testimonials-section').forEach((section) => {
		const el = section.querySelector('.js-client-testimonials-swiper');
		if (!el || el.swiper) {
			return;
		}

		initSectionSwiper(section);
	});
}
